import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';

// One QueryClient for the app. Lives here (not inline in main.tsx) so the auth layer can reach in
// and bust the cache on logout / user-switch — see clearAppCache below.
//
// staleTime/gcTime tune the stale-while-revalidate feel. staleTime is a generous 5 MINUTES (was 30s):
// a revisit within 5 min is instant with NO refetch at all, and PAST it the query is served from cache
// (memory or the localStorage persister) INSTANTLY while a single background refetch reconciles — the
// user never waits on a spinner for data they've already seen. Mutations invalidate their own keys, so
// a stale window never shows a user their own action as out-of-date; it only defers picking up changes
// made elsewhere (another user, a background job), which SWR then folds in silently.
//
// NOTE: this cache is per-browser — it can't serve one user's data to a *different* user. True
// cross-user "cache the federation's clip list once" needs a server-side layer (HTTP Cache-Control /
// the Redis cache in §8/§12), which isn't scheduled until M9.
//
// gcTime is a full DAY to match the persister's maxAge — a query garbage-collected from memory sooner
// than the on-disk copy expires could never be restored, so the two windows are deliberately equal.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60_000,
      gcTime: 1000 * 60 * 60 * 24,
    },
  },
});

// Persist the whole query cache to localStorage. The cache is written back (throttled) on every
// query change and rehydrated synchronously at boot, so a page reload paints the last-seen data
// INSTANTLY while a background refetch (everything is stale past staleTime) pulls the real data —
// the good-UX ask: show something immediately, reconcile silently. Sync (not async/IndexedDB)
// storage keeps the restore on the critical path so there's no first-paint flash of empty state.
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'proref.query-cache',
});

// Bump when the persisted shape changes in a breaking way (or a release must hard-invalidate every
// client's cache). A mismatch makes the persister discard the stored cache on restore.
//
// v2 (2026-09-02): the referee analytics payload gained activity/exams/coverage. Rehydration is
// SYNCHRONOUS and bypasses the queryFn entirely, so a returning client paints last week's object
// shape through today's components before any refetch can correct it — which crashed the profile
// page outright. Consumers of persisted data must also tolerate a missing field (the profile page
// treats one as "still loading", which is exactly what it is); this buster is what stops the whole
// population carrying a stale shape forward indefinitely.
//
// v3 (2026-09-03): Webinar gained platform/speakers/join_url/has_join_url. A cache written before
// that restores webinar rows with NO `speakers` key, and `WebinarRow` reads `.length` off it — the
// same crash v2 fixed for the analytics payload, in a different component.
const CACHE_BUSTER = 'v3';

// localStorage key holding the id of the account the persisted cache belongs to — the guard that
// stops one user's cache from restoring under another on a shared device (multi-tenant isolation,
// CLAUDE.md §6). Not the cache itself, just its owner tag.
const CACHE_OWNER_KEY = 'proref.query-cache.owner';

export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  // Discard anything older than a day so a long-idle device never restores stale-beyond-useful data
  // (also the ceiling for how long a signed playback URL etc. could linger before it's refetched).
  maxAge: 1000 * 60 * 60 * 24,
  buster: CACHE_BUSTER,
};

// Wipe both the in-memory and the on-disk cache. Called on logout and on the auth:logout event so a
// dead session leaves nothing behind for the next person on the device to restore.
export function clearAppCache(): void {
  queryClient.clear();
  window.localStorage.removeItem(CACHE_OWNER_KEY);
  void persister.removeClient();
}

// Tag the persisted cache with the signed-in user and, if it previously belonged to a DIFFERENT
// user, wipe it first. Call on every auth resolve (boot/login/signup): same user → cache is kept
// (that's the whole point — instant data on their reload); different user → cleared before any of
// their tenant-scoped queries hydrate.
export function ensureCacheOwner(userId: string): void {
  const previous = window.localStorage.getItem(CACHE_OWNER_KEY);
  if (previous && previous !== userId) clearAppCache();
  window.localStorage.setItem(CACHE_OWNER_KEY, userId);
}
